import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ChartEmptyState from './ChartEmptyState';
import { truncateChartLabel } from '../utils/chartLabels';
import './AssistantsPieChart.css';

const COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#6366f1',
  '#ef4444',
  '#06b6d4',
  '#8b5cf6',
  '#ec4899',
];

const AssistantsPieChart = ({
  data,
  height = 300,
  stretch = false,
  legendPosition = 'bottom',
  emptyState = null,
  onSliceClick,
}) => {
  const chartData = data && data.length > 0 ? data.slice(0, 10) : [];

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 720 : false,
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const color = payload[0].color || '#000';
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip__header">
          <span className="chart-tooltip__swatch" style={{ background: color }} />
          <div>
            <div className="chart-tooltip__title">{d.name}</div>
            {d.model ? (
              <div className="chart-tooltip__subtitle">{d.model}</div>
            ) : null}
          </div>
        </div>
        <div className="chart-tooltip__row">
          Requests: <strong>{d.requests.toLocaleString()}</strong>
        </div>
        <div className="chart-tooltip__row">
          Share: <strong>{d.value}%</strong>
        </div>
      </div>
    );
  };

  const renderCustomLabel = useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.08) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)', pointerEvents: 'none' }}
      >
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  }, []);

  const isFixedHeight = typeof height === 'number';
  const useBottomLegend = legendPosition === 'bottom' || isNarrow;
  const chartHeight = isFixedHeight ? Math.max(220, height - (useBottomLegend ? 120 : 0)) : height;
  const outerRadius = isFixedHeight
    ? Math.max(72, Math.min(Math.floor(chartHeight * 0.38), 128))
    : '42%';
  const innerRadius = typeof outerRadius === 'number'
    ? Math.max(Math.round(outerRadius * 0.52), 36)
    : '22%';

  const wrapperStyle = isFixedHeight
    ? { width: '100%', height, minHeight: height, boxSizing: 'border-box' }
    : (stretch
      ? { width: '100%', flex: 1, minHeight: 220, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }
      : { width: '100%', minHeight: 220, height: height || '100%', boxSizing: 'border-box' });

  const Legend = ({ items }) => (
    <div className={`assistants-pie-legend${useBottomLegend ? ' assistants-pie-legend--bottom' : ''}`}>
      {items.map((entry, i) => {
        const color = COLORS[i % COLORS.length];
        const label = truncateChartLabel(entry.name, 28);
        const modelLabel = entry.model && entry.model !== 'N/A'
          ? truncateChartLabel(entry.model, 32)
          : null;
        return (
          <Tooltip
            key={`${entry.name}-${i}`}
            title={(
              <div>
                <div>{entry.name}</div>
                {entry.model ? <div style={{ opacity: 0.85 }}>{entry.model}</div> : null}
                <div style={{ marginTop: 4 }}>
                  {entry.value}% · {entry.requests.toLocaleString()} requests
                </div>
              </div>
            )}
          >
            <div className="assistants-pie-legend__item">
              <span className="assistants-pie-legend__swatch" style={{ background: color }} />
              <span className="assistants-pie-legend__text">
                <span className="assistants-pie-legend__name">{label}</span>
                {modelLabel ? (
                  <span className="assistants-pie-legend__model">{modelLabel}</span>
                ) : null}
                <span className="assistants-pie-legend__meta">
                  {entry.value}% · {entry.requests.toLocaleString()} req
                </span>
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );

  if (emptyState) {
    return (
      <ChartEmptyState
        icon={PieChartOutlined}
        title={emptyState.title}
        description={emptyState.description}
        actionLabel={emptyState.actionLabel}
        onAction={emptyState.onAction}
        tone={emptyState.tone}
        height={height}
        minHeight={220}
      />
    );
  }

  if (!chartData.length) return null;

  return (
    <div
      className="assistants-pie-chart"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        ...wrapperStyle,
      }}
    >
      <div className="assistants-pie-chart__plot" style={{ height: chartHeight, minHeight: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              dataKey="value"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth={2}
              isAnimationActive={false}
              onClick={onSliceClick ? (_, index) => onSliceClick(chartData[index]) : undefined}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
                />
              ))}
            </Pie>
            <RechartsTooltip content={customTooltip} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <Legend items={chartData} />
    </div>
  );
};

export default AssistantsPieChart;
